/**
 * OASIS form section sidebar. Each section shows at most one status dot:
 * red for local CMS fatals, amber for advisory items (warnings/contradictions)
 * when fatals are clear globally, otherwise purple when the section has
 * meaningful answers (see `OASISNavigationAnswered`).
 */
import React, { useEffect, useRef } from 'react';
import { OASISSchema, OASISFormData, OASISNavigationItem } from './types';
import {
  ACTIVE_DIAGNOSIS_CATEGORY,
  resolveCategoryLabel,
} from './OASISFormLabels';
import { countNavigationAnsweredQuestions } from './OASISNavigationAnswered';

interface OASISNavigationProps {
  categories: string[];
  currentCategory: string | null;
  onCategoryChange: (category: string) => void;
  schema: OASISSchema;
  formData: OASISFormData;
  /** CMS fatals mapped to this section — red sidebar mark. */
  fatalCountByCategory?: Record<string, number>;
  /**
   * CMS warnings + contradictions mapped to this section. Only pass when
   * global fatals are clear (advisory mode); amber sidebar mark.
   */
  advisoryCountByCategory?: Record<string, number>;
  /**
   * `section_id → display_name` lookup sourced from
   * `FormSchemaVersion.section_order` (ROG-2295). When empty, labels fall
   * back to the legacy regex transform inside `resolveCategoryLabel`.
   */
  labelByCategory?: Record<string, string>;
}

export const OASISNavigation: React.FC<OASISNavigationProps> = ({
  categories,
  currentCategory,
  onCategoryChange,
  schema,
  formData,
  fatalCountByCategory = {},
  advisoryCountByCategory = {},
  labelByCategory = {},
}) => {
  const getNavigationItems = (): OASISNavigationItem[] => {
    return categories.map(category => {
      if (category === ACTIVE_DIAGNOSIS_CATEGORY) {
        return {
          category,
          label: 'Active Diagnosis',
          isCompleted: false,
          questionCount: 0,
          answeredCount: 0,
        };
      }

      const questions = schema[category] || [];
      const questionCount = questions.length;
      const answeredCount = countNavigationAnsweredQuestions(
        questions,
        formData
      );

      return {
        category,
        label: resolveCategoryLabel(category, labelByCategory),
        isCompleted: answeredCount === questionCount && questionCount > 0,
        questionCount,
        answeredCount,
      };
    });
  };

  const navigationItems = getNavigationItems();
  const activeItemRef = useRef<HTMLButtonElement | null>(null);

  // The list scrolls inside itself once it is pinned and taller than the
  // scrollport, so a section restored from deep in the list would otherwise
  // open with its own entry out of sight. `nearest` leaves outer scrollers
  // alone when the entry is already visible.
  useEffect(() => {
    // Optional call: jsdom and older embedded webviews do not implement it.
    activeItemRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [currentCategory]);

  return (
    <div className='space-y-0.5'>
      {navigationItems.map(item => {
        const isActive = currentCategory === item.category;
        const hasAnswers = item.answeredCount > 0;
        const hasFatal = (fatalCountByCategory[item.category] ?? 0) > 0;
        const hasAdvisory = (advisoryCountByCategory[item.category] ?? 0) > 0;
        return (
          <button
            key={item.category}
            ref={isActive ? activeItemRef : undefined}
            type='button'
            aria-current={isActive ? 'step' : undefined}
            onClick={() => onCategoryChange(item.category)}
            className={`w-full text-left px-2.5 py-2 rounded-md text-xs leading-snug transition-colors flex items-center justify-between gap-2 ${
              isActive
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <span className='flex-1 min-w-0'>{item.label}</span>

            <span className='flex items-center gap-1.5 self-center overflow-visible'>
              {hasFatal ? (
                <div
                  className='w-1.5 h-1.5 rounded-full shrink-0 bg-destructive'
                  title='Has blocking scrubber issues'
                />
              ) : hasAdvisory ? (
                <div
                  className='w-1.5 h-1.5 rounded-full shrink-0 bg-amber-500'
                  title='Has advisory scrubber items'
                />
              ) : hasAnswers ? (
                <div
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-primary/70' : 'bg-primary/40'}`}
                  title='Has answers'
                />
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};
